/**
 * SEO Component using React 19's native document metadata support
 * React 19 automatically hoists <title>, <meta>, and <link> to <head>
 */
const SEO = ({
  title = "sQawk | Social Media for People",
  description = "sQawk is a next-generation social platform inspired by what worked in the early days of social media. Conversation beats clout. Community beats virality. Authenticity beats algorithms.",
  image = "https://www.sqawk.app/sqawk-og-image.png",
  url = "https://sqawk.app",
  type = "website",
  keywords = "sQawk, social media, authentic social network, community, conversation, no algorithms, travel friends, collect cities",
  noIndex = false,
}) => {
  const fullTitle = title.includes("sQawk") ? title : `${title} | sQawk`;

  return (
    <>
      {/* React 19 native metadata - automatically hoisted to <head> */}
      <title>{fullTitle}</title>
      <meta name="title" content={fullTitle} />
      <meta name="description" content={description} />
      <meta name="keywords" content={keywords} />
      {noIndex ? (
        <meta name="robots" content="noindex, nofollow" />
      ) : (
        <meta name="robots" content="index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1" />
      )}
      <link rel="canonical" href={url} />

      {/* Open Graph */}
      <meta property="og:type" content={type} />
      <meta property="og:url" content={url} />
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={description} />
      <meta property="og:image" content={image} />

      {/* Twitter Card */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:url" content={url} />
      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={image} />
    </>
  );
};

// Pre-configured SEO for common pages
export const PageSEO = {
  Home: () => (
    <SEO
      title="sQawk | Social Media for People"
      description="Join sQawk, the next-generation social platform where conversation beats clout, community beats virality, and authenticity beats algorithms. Connect with travelers, claim your city, join communities. Social Media for People."
      url="https://sqawk.app"
      keywords="sQawk, social media, authentic social network, community, no algorithms, real connections, travel friends, collect cities, claim your city"
    />
  ),
  
  Communities: () => (
    <SEO
      title="Communities - sQawk Trends, City sQawk & Events"
      description="Explore sQawk communities: trending topics from Google Trends, city-based local communities where you can claim your hometown, and upcoming events."
      url="https://sqawk.app/communities"
      keywords="sQawk communities, city communities, local social network, events, trends, community chat, claim your city, city sQawk"
    />
  ),
  
  Travel: () => (
    <SEO
      title="Travel Friends - Find Travel Companions on sQawk"
      description="Connect with fellow travelers on sQawk. Share your travel experiences, find travel buddies, discover verified travel agents, and collect cities you've visited."
      url="https://sqawk.app/travel-friends"
      keywords="travel friends, travel companions, travel social network, collect cities, travel community, verified travel agents"
    />
  ),
  
  Directory: () => (
    <SEO
      title="100,000 Global Business Directory - List Your Business Worldwide"
      description="Join the sQawk 100,000 Global Business Directory! Get your business featured globally. Be part of our goal to host 100,000 businesses worldwide."
      url="https://sqawk.app/directory"
      keywords="business directory, global business listing, 100000 business directory, small business promotion, worldwide business"
    />
  ),
  
  Sports247: () => (
    <SEO
      title="24/7 Sports - Live Scores, Stats & Sports Community"
      description="Your 24/7 sports hub on sQawk. Get live scores, player stats, favorite team tracking, and connect with fellow sports fans."
      url="https://sqawk.app/sports247"
      keywords="sports, live scores, NFL, NBA, MLB, NHL, soccer, sports community, sports fans"
    />
  ),
  
  SqawkerSkills: () => (
    <SEO
      title="Sqawker Skills - Verified Professionals Directory"
      description="Find verified professionals on sQawk: Real Estate Agents, Travel Agents, Personal Trainers, Financial Advisors, Life Coaches, Lawyers, Event Planners, and Interior Designers."
      url="https://sqawk.app/sqawker-skills"
      keywords="verified professionals, real estate agents, travel agents, personal trainers, financial advisors, life coaches, lawyers"
    />
  ),
  
  Profile: (username, name, bio = "") => (
    <SEO
      title={`${name || username} - sQawk Profile`}
      description={bio || `Check out ${name || username}'s profile on sQawk. Connect, chat, and see their digital business card.`}
      url={`https://sqawk.app/profile/${username}`}
      type="profile"
    />
  ),
  
  MyBusiness: () => (
    <SEO
      title="My Business - Manage Your Business Profile"
      description="Manage your business profile on sQawk. Update your business information, track analytics, and connect with customers."
      url="https://sqawk.app/my-business"
      noIndex={true}
    />
  ),
  
  Settings: () => (
    <SEO
      title="Settings - sQawk"
      description="Manage your sQawk account settings, profile, notifications, and preferences."
      url="https://sqawk.app/settings"
      noIndex={true}
    />
  ),
  
  Admin: () => (
    <SEO
      title="Admin Dashboard - sQawk"
      description="sQawk admin dashboard for managing users, communities, and content."
      url="https://sqawk.app/admin"
      noIndex={true}
    />
  ),
  
  Notifications: () => (
    <SEO
      title="Notifications - sQawk"
      description="View your notifications on sQawk - friend requests, messages, likes, and more."
      url="https://sqawk.app/notifications"
      noIndex={true}
    />
  ),
  
  Messages: () => (
    <SEO
      title="Messages - sQawk"
      description="Your private messages on sQawk. Connect and chat with friends."
      url="https://sqawk.app/messages"
      noIndex={true}
    />
  ),

  Dashboard: () => (
    <SEO
      title="Dashboard - sQawk"
      description="Your sQawk dashboard - view your feed, connect with friends, and share with the world."
      url="https://sqawk.app/dashboard"
      noIndex={true}
    />
  ),

  Login: () => (
    <SEO
      title="Login - sQawk | Social Media for People"
      description="Log in to sQawk - Social Media for People. Connect with friends, join communities, and share your world."
      url="https://sqawk.app/login"
      keywords="sQawk login, sign in, social media login"
    />
  ),

  Register: () => (
    <SEO
      title="Sign Up - Join sQawk Today"
      description="Create your free sQawk account. Join the social network where conversation beats clout and community beats virality. Social Media for People."
      url="https://sqawk.app/register"
      keywords="sQawk sign up, create account, join social network, register"
    />
  ),

  Referrals: () => (
    <SEO
      title="Share & Referrals - Invite Friends to sQawk"
      description="Invite friends to sQawk and earn rewards. Share your unique referral code and help grow the community."
      url="https://sqawk.app/referrals"
      noIndex={true}
    />
  ),

  ForgotPassword: () => (
    <SEO
      title="Forgot Password - sQawk"
      description="Reset your sQawk password. Enter your email to receive a password reset link."
      url="https://sqawk.app/forgot-password"
      noIndex={true}
    />
  ),

  NonProfits: () => (
    <SEO
      title="sQawk Non-Profits - 501(c)(3) Directory & Events"
      description="Discover verified non-profit organizations on sQawk. Browse the directory, attend fundraising events, donate, and support causes you believe in."
      url="https://sqawk.app/nonprofits"
      keywords="non-profit, charity, 501c3, fundraising, donate, sQawk non-profits, community giving, events"
    />
  ),

  Events: () => (
    <SEO
      title="sQawk Events - Discover Community & Non-Profit Events"
      description="Find and attend community events, non-profit fundraisers, workshops, and gatherings on sQawk. In-person and virtual events near you."
      url="https://sqawk.app/events"
      keywords="events, community events, non-profit events, fundraisers, workshops, virtual events, sQawk events"
    />
  ),

  Search: () => (
    <SEO
      title="Search - sQawk"
      description="Search for people, posts, and communities on sQawk."
      url="https://sqawk.app/search"
      noIndex={true}
    />
  ),

  Trending: () => (
    <SEO
      title="sQawk AI - Your Universal Life Assistant"
      description="Discover trending topics, hashtags, and conversations on sQawk. See what the community is talking about."
      url="https://sqawk.app/trending"
      keywords="sQawk trending, trending topics, popular hashtags, viral posts"
    />
  ),

  Hashtag: (tag) => (
    <SEO
      title={`#${tag} - sQawk`}
      description={`Explore posts tagged with #${tag} on sQawk. Join the conversation and share your thoughts.`}
      url={`https://sqawk.app/hashtag/${tag}`}
      keywords={`${tag}, sQawk hashtag, trending topics`}
    />
  ),

  MySchedule: () => (
    <SEO
      title="My Schedule - sQawk"
      description="Manage your appointments and schedule on sQawk."
      url="https://sqawk.app/my-schedule"
      noIndex={true}
    />
  ),

  BookAppointment: (username, name) => (
    <SEO
      title={`Book with ${name || username} - sQawk`}
      description={`Schedule an appointment with ${name || username} on sQawk. Easy online booking.`}
      url={`https://sqawk.app/book/${username}`}
      keywords="book appointment, schedule meeting, online booking, sQawk"
    />
  ),

  ContactForm: (username, name) => (
    <SEO
      title={`Contact ${name || username} - sQawk`}
      description={`Get in touch with ${name || username} on sQawk.`}
      url={`https://sqawk.app/contact/${username}`}
      noIndex={true}
    />
  ),

  BusinessHub: () => (
    <SEO
      title="Business Hub - Manage Your Business & Skills on sQawk"
      description="Your sQawk Business Hub. Manage your business profile, verified skills, QR codes, events, and explore the global directory."
      url="https://sqawk.app/business-hub"
      noIndex={true}
    />
  ),

  BusinessFeed: () => (
    <SEO
      title="Business Community Feed - sQawk"
      description="Connect with business owners on sQawk. Share business insights, find partners, and grow your network."
      url="https://sqawk.app/business-feed"
      noIndex={true}
    />
  ),

  Friends: () => (
    <SEO
      title="sQawk Friends - Connect & Discover People"
      description="Find and connect with friends on sQawk. View profiles, message, video call, and discover new people in your city and beyond."
      url="https://sqawk.app/friends"
      keywords="sQawk friends, find friends, social connections, discover people, friend profiles, video call"
    />
  ),

  CommunityFeeds: () => (
    <SEO
      title="sQawk Community Feeds - Latest Posts & Conversations"
      description="Browse the latest community feeds on sQawk. Join conversations, share updates, and connect with people worldwide."
      url="https://sqawk.app/communities?tab=feeds"
      keywords="sQawk feeds, community posts, social feed, conversations, updates"
    />
  ),

  CommunityThemes: () => (
    <SEO
      title="sQawk Themes - Trending Topics & Discussions"
      description="Explore trending themes and topics on sQawk. Join themed discussions and discover what the community is talking about."
      url="https://sqawk.app/communities?tab=trends"
      keywords="sQawk themes, trending topics, themed discussions, community topics"
    />
  ),

  CityCommunities: () => (
    <SEO
      title="City Communities - Connect Locally on sQawk"
      description="Join city-based communities on sQawk. Find people in your city, pin your hometown, and engage in local conversations."
      url="https://sqawk.app/communities?tab=cities"
      keywords="city communities, local social network, hometown community, claim your city, local connections"
    />
  ),

  CommunityEvents: () => (
    <SEO
      title="sQawk Events - Community Gatherings & Meetups"
      description="Discover community events on sQawk. Find meetups, workshops, and gatherings in your city and online."
      url="https://sqawk.app/communities?tab=events"
      keywords="sQawk events, community events, meetups, workshops, local events, online gatherings"
    />
  ),

  CommunityChat: (name) => (
    <SEO
      title={`${name || "Community"} - sQawk Community Chat`}
      description={`Join the ${name || "community"} chat on sQawk. Connect with members, share updates, and be part of the conversation.`}
      url={`https://sqawk.app/community/${name}`}
      keywords={`sQawk community, ${name}, community chat, local community`}
    />
  ),

  NonProfitDetail: (name) => (
    <SEO
      title={`${name || "Non-Profit"} - sQawk Non-Profit Directory`}
      description={`Learn about ${name || "this organization"} on sQawk. Support their mission, donate, and attend their events.`}
      url={`https://sqawk.app/nonprofits`}
      keywords={`${name}, non-profit, charity, donate, sQawk`}
    />
  ),

  BusinessHubDirectory: () => (
    <SEO
      title="100,000 Global Business Directory - sQawk Business Hub"
      description="Browse the sQawk 100,000 Global Business Directory. Find and connect with businesses worldwide."
      url="https://sqawk.app/business-hub?tab=directory"
      keywords="business directory, global directory, sQawk business hub, find businesses"
    />
  ),

  BusinessHubJobs: () => (
    <SEO
      title="Job Opportunities - sQawk Business Hub"
      description="Find job opportunities on sQawk. Browse listings from businesses and professionals in the community."
      url="https://sqawk.app/business-hub?tab=hiring"
      keywords="jobs, job opportunities, hiring, sQawk jobs, career"
    />
  ),

  LiveStream: (title) => (
    <SEO
      title={`${title || "Live Stream"} - sQawk Live`}
      description={`Watch live on sQawk. Join the live stream and interact with the community in real-time.`}
      url="https://sqawk.app/live"
      keywords="live stream, sQawk live, live video, broadcast"
    />
  ),

  ThemedCommunity: (name) => (
    <SEO
      title={`${name || "Community"} - sQawk Themed Community`}
      description={`Join the ${name || "community"} on sQawk. Connect with like-minded people, share posts, and engage in discussions.`}
      url={`https://sqawk.app/community/${encodeURIComponent(name || "")}`}
      keywords={`sQawk community, ${name || ""}, themed community, discussion, social group`}
    />
  ),

  EventCommunity: (title) => (
    <SEO
      title={`${title || "Event"} Community - sQawk`}
      description={`Join the event community for ${title || "this event"} on sQawk. Chat with attendees, share updates, and stay connected.`}
      url="https://sqawk.app/event-community"
      keywords="event community, sQawk event, event chat, attendees"
      noIndex={true}
    />
  ),

  NpoEventDetail: (name) => (
    <SEO
      title={`${name || "Non-Profit Event"} - sQawk Events`}
      description={`Attend ${name || "this non-profit event"} on sQawk. Support the cause, donate, and connect with the community.`}
      url="https://sqawk.app/npo-event"
      keywords={`non-profit event, fundraiser, ${name || ""}, sQawk events, charity event`}
    />
  ),

  ResetPassword: () => (
    <SEO
      title="Reset Password - sQawk"
      description="Reset your sQawk account password."
      url="https://sqawk.app/reset-password"
      noIndex={true}
    />
  ),

  PendingApproval: () => (
    <SEO
      title="Account Pending Approval - sQawk"
      description="Your sQawk account is being reviewed."
      url="https://sqawk.app/pending-approval"
      noIndex={true}
    />
  ),

  CallPage: () => (
    <SEO
      title="Call - sQawk"
      description="Voice and video calling on sQawk."
      url="https://sqawk.app/call"
      noIndex={true}
    />
  ),

  CreditsSuccess: () => (
    <SEO
      title="Purchase Complete - sQawk Credits"
      description="Your sQawk credit purchase was successful."
      url="https://sqawk.app/credits/success"
      noIndex={true}
    />
  ),

  NpoEventWizard: () => (
    <SEO
      title="Create Non-Profit Event - sQawk"
      description="Create a fundraising or awareness event for your non-profit on sQawk."
      url="https://sqawk.app/npo-event-wizard"
      noIndex={true}
    />
  ),

  NpoManageTab: () => (
    <SEO
      title="Manage Non-Profit - sQawk"
      description="Manage your non-profit organization on sQawk."
      url="https://sqawk.app/npo-manage"
      noIndex={true}
    />
  ),

  AdminSubscriptions: () => (
    <SEO
      title="Subscription Management - sQawk Admin"
      description="Admin subscription management for sQawk."
      url="https://sqawk.app/admin/subscriptions"
      noIndex={true}
    />
  ),

  TermsOfService: () => (
    <SEO
      title="Terms of Service - sQawk"
      description="Read the sQawk Terms of Service. Learn about our policies, user agreements, and community guidelines."
      url="https://sqawk.app/terms"
      keywords="sQawk terms, terms of service, user agreement, community guidelines, privacy"
    />
  ),
};

export default SEO;
